import { ChevronDown, ChevronRight, Cpu } from "lucide-react";
import { type ReactNode, useState } from "react";

export interface ContextUsageProps {
  contextWindow: number;
  maxOutputTokens: number;
  toolTokens: number;
  budget: number;
  promptTokens: number;
  percentUsed: number;
  messagesLoaded: number;
  messagesAfterTrim: number;
  model: string;

  groupsPruned?: number;
  groupsTrimmed?: number;
  summarized?: boolean;

  defaultOpen?: boolean;

  /** Overrides for the built-in strings (see DEFAULT_CONTEXT_USAGE_LABELS). */
  labels?: Partial<ContextUsageLabels>;
  /** Formats token counts. Defaults to a compact English format ("3.9K", "1.2M"). */
  formatTokens?: (n: number) => string;
}

/** Every built-in string, so apps can translate them. Templated ones take the (already formatted) value. */
export interface ContextUsageLabels {
  tokens: (count: string) => string;
  percentUsed: (percent: number) => string;
  pruned: (count: number) => string;
  trimmed: (count: number) => string;
  summarized: string;
  contextWindow: string;
  maxOutput: string;
  toolDefinitions: string;
  availableBudget: string;
  promptTokens: string;
  messages: string;
  messagesLoaded: (count: number) => string;
  compactionPipeline: string;
  groupsPruned: (count: number) => string;
  groupsTrimmed: (count: number) => string;
}

export const DEFAULT_CONTEXT_USAGE_LABELS: ContextUsageLabels = {
  tokens: (count) => `${count} tokens`,
  percentUsed: (percent) => `${percent}% used`,
  pruned: (count) => `${count} pruned`,
  trimmed: (count) => `${count} trimmed`,
  summarized: "summarized",
  contextWindow: "Context window",
  maxOutput: "Max output",
  toolDefinitions: "Tool definitions",
  availableBudget: "Available budget",
  promptTokens: "Prompt tokens",
  messages: "Messages",
  messagesLoaded: (count) => ` / ${count} loaded`,
  compactionPipeline: "Compaction pipeline",
  groupsPruned: (count) => `${count} groups pruned`,
  groupsTrimmed: (count) => `${count} groups trimmed`,
};

type UsageStatus = "success" | "warning" | "error";

// Severity is carried by fills (status dot, progress bar), never by text color: the status text tokens
// don't reach 4.5:1 on dark surfaces at this size, while fills only need 3:1 as non-text marks.
const USAGE_FILL_CLASSES: Record<UsageStatus, string> = {
  success: "bg-success-fill",
  warning: "bg-warning-fill",
  error: "bg-error-fill",
};

function usageStatus(percent: number): UsageStatus {
  if (percent >= 90) return "error";
  if (percent >= 70) return "warning";
  return "success";
}

function defaultFormatTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
}

function PipelineChip({ dotClass, children }: Readonly<{ dotClass?: string; children: ReactNode }>) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded bg-surface-base px-1.5 py-0.5 text-content-secondary">
      {dotClass && <span aria-hidden="true" className={`size-1.5 shrink-0 rounded-full ${dotClass}`} />}
      {children}
    </span>
  );
}

export function ContextUsage({
  contextWindow,
  maxOutputTokens,
  toolTokens,
  budget,
  promptTokens,
  percentUsed,
  messagesLoaded,
  messagesAfterTrim,
  model,
  groupsPruned = 0,
  groupsTrimmed = 0,
  summarized = false,
  defaultOpen = false,
  labels,
  formatTokens = defaultFormatTokens,
}: Readonly<ContextUsageProps>) {
  const l = { ...DEFAULT_CONTEXT_USAGE_LABELS, ...labels };
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const pct = Math.round(Math.min(percentUsed, 100));
  const status = usageStatus(pct);
  const hadActivity = groupsPruned > 0 || groupsTrimmed > 0 || summarized;

  return (
    <div className="rounded-lg border border-border-subtle bg-surface-secondary font-mono text-xs">
      <button
        type="button"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((open) => !open)}
        className="flex w-full items-center gap-2 px-3 py-1.5 text-content-secondary transition-colors hover:text-content-primary"
      >
        {isOpen ? <ChevronDown size={12} className="shrink-0" /> : <ChevronRight size={12} className="shrink-0" />}
        <Cpu size={12} className="shrink-0" />
        <span aria-hidden="true" className={`size-1.5 shrink-0 rounded-full ${USAGE_FILL_CLASSES[status]}`} />
        <span className="text-content-primary">{l.tokens(formatTokens(promptTokens))}</span>
        <span className="text-content-tertiary">·</span>
        <span className="text-content-primary">{l.percentUsed(pct)}</span>
        {hadActivity && (
          <>
            <span className="text-content-tertiary">·</span>
            <span className="truncate text-content-tertiary">
              {[
                groupsPruned > 0 ? l.pruned(groupsPruned) : null,
                groupsTrimmed > 0 ? l.trimmed(groupsTrimmed) : null,
                summarized ? l.summarized : null,
              ]
                .filter(Boolean)
                .join(", ")}
            </span>
          </>
        )}
        <span className="ml-auto shrink-0 text-content-tertiary">{model}</span>
      </button>

      {isOpen && (
        <div className="space-y-2 border-t border-border-subtle px-3 py-2">
          <div className="flex items-center gap-2">
            <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-surface-base">
              <div
                className={`h-full rounded-full transition-all ${USAGE_FILL_CLASSES[status]}`}
                style={{ width: `${pct}%` }}
              />
            </div>
            <span className="shrink-0 text-content-primary">{pct}%</span>
          </div>

          <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-content-secondary">
            <div className="flex justify-between">
              <span>{l.contextWindow}</span>
              <span className="text-content-primary">{formatTokens(contextWindow)}</span>
            </div>
            <div className="flex justify-between">
              <span>{l.maxOutput}</span>
              <span className="text-content-primary">{formatTokens(maxOutputTokens)}</span>
            </div>
            <div className="flex justify-between">
              <span>{l.toolDefinitions}</span>
              <span className="text-content-primary">{formatTokens(toolTokens)}</span>
            </div>
            <div className="flex justify-between">
              <span>{l.availableBudget}</span>
              <span className="text-content-primary">{formatTokens(budget)}</span>
            </div>
            <div className="flex justify-between">
              <span>{l.promptTokens}</span>
              <span className="text-content-primary">{formatTokens(promptTokens)}</span>
            </div>
            <div className="flex justify-between">
              <span>{l.messages}</span>
              <span className="text-content-primary">
                {messagesAfterTrim}
                {messagesLoaded !== messagesAfterTrim && (
                  <span className="text-content-tertiary">{l.messagesLoaded(messagesLoaded)}</span>
                )}
              </span>
            </div>
          </div>

          {hadActivity && (
            <div className="space-y-1 border-t border-border-subtle pt-2">
              <span className="text-content-tertiary">{l.compactionPipeline}</span>
              <div className="flex flex-wrap gap-2">
                {groupsPruned > 0 && (
                  <PipelineChip dotClass="bg-info-fill">{l.groupsPruned(groupsPruned)}</PipelineChip>
                )}
                {summarized && <PipelineChip>{l.summarized}</PipelineChip>}
                {groupsTrimmed > 0 && (
                  <PipelineChip dotClass="bg-warning-fill">{l.groupsTrimmed(groupsTrimmed)}</PipelineChip>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
